import { Injectable, inject, signal, computed, Injector, runInInjectionContext } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Dialog } from '@angular/cdk/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent, LanguageService, ThemeService, type ConfirmDialogData } from '@zag/ui';
import { type AuthSession, type AuthUser, type SignInCredentials, type SignUpCredentials } from '@zag/auth/models';
import { JobsGatewayClient } from '@zag/jobs/ui';
import { PermissionService } from './permission.service';
import { UsersMeService } from './users-me.service';
import { NotificationGatewayClient, NotificationsService } from '@zag/notifications/ui/data-access';

export type { AuthSession as Session, AuthUser as User, SignInCredentials, SignUpCredentials } from '@zag/auth/models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  // Lazy inject PermissionService to avoid circular dependency
  private _permissionService: PermissionService | null = null;
  private get permissionService(): PermissionService {
    this._permissionService ??= runInInjectionContext(this.injector, () => inject(PermissionService));
    return this._permissionService;
  }

  // Lazy inject UsersMeService to reload profile after login
  private _usersMeService: UsersMeService | null = null;
  private get usersMeService(): UsersMeService {
    this._usersMeService ??= runInInjectionContext(this.injector, () => inject(UsersMeService));
    return this._usersMeService;
  }

  // Lazy + optional so auth never hard-depends on the notifications bundle.
  private _notificationGateway: NotificationGatewayClient | null = null;
  private get notificationGateway(): NotificationGatewayClient | null {
    this._notificationGateway ??= runInInjectionContext(this.injector, () => inject(NotificationGatewayClient, { optional: true }));
    return this._notificationGateway;
  }

  private _jobsGateway: JobsGatewayClient | null = null;
  private get jobsGateway(): JobsGatewayClient | null {
    this._jobsGateway ??= runInInjectionContext(this.injector, () => inject(JobsGatewayClient, { optional: true }));
    return this._jobsGateway;
  }

  private _notificationsService: NotificationsService | null = null;
  private get notificationsService(): NotificationsService | null {
    this._notificationsService ??= runInInjectionContext(this.injector, () => inject(NotificationsService, { optional: true }));
    return this._notificationsService;
  }

  // Signal-based state
  private readonly _currentUser = signal<AuthUser | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _isSigningOut = signal(false);
  private readonly _error = signal<string | null>(null);
  private authOperation = 0;

  // Public readonly accessors
  readonly currentUser = this._currentUser.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  // True from the moment a sign-out is initiated until the server-side
  // session cleanup completes. Layouts use this flag to hide protected
  // chrome immediately and prevent visual artifacts during the transition.
  readonly isSigningOut = this._isSigningOut.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed state
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  /** Sign in using configured email or username credentials. */
  async signIn(credentials: SignInCredentials, returnUrl?: string): Promise<void> {
    const operation = ++this.authOperation;
    let authenticated = false;
    this._isLoading.set(true);
    this._isSigningOut.set(false);
    this._error.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<AuthSession>(
          credentials.email ? '/api/auth/sign-in/email' : '/api/auth/sign-in/username',
          credentials.email
            ? { email: credentials.email, password: credentials.password, rememberMe: credentials.rememberMe ?? false }
            : { username: credentials.username, password: credentials.password, rememberMe: credentials.rememberMe ?? false }
        )
      );

      authenticated = true;
      if (operation !== this.authOperation) return;

      this._currentUser.set(response.user);
      this.jobsGateway?.reconnect();

      // Load user permissions and profile after successful login
      this.permissionService.loadPermissions();
      this.usersMeService.reloadProfile();
      void this.usersMeService.syncServerPreferences();
      void this.notificationGateway?.reconnect();
      this.notificationsService?.refresh();

      // Navigate to returnUrl if provided, otherwise to root (smart redirect will handle)
      await this.router.navigateByUrl(returnUrl || '/');
    } catch (error: unknown) {
      if (operation === this.authOperation) {
        if (!authenticated) this.jobsGateway?.reset();
        const errorMessage = this.extractErrorMessage(error, 'Sign in failed. Please check your credentials.');
        this._error.set(errorMessage);
      }
      throw error;
    } finally {
      if (operation === this.authOperation) this._isLoading.set(false);
    }
  }

  /** Sign up using configured email or username credentials. */
  async signUp(credentials: SignUpCredentials): Promise<void> {
    const operation = ++this.authOperation;
    let authenticated = false;
    this._isLoading.set(true);
    this._isSigningOut.set(false);
    this._error.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<AuthSession>('/api/auth/sign-up/email', {
          name: credentials.name,
          email: credentials.email ?? `${credentials.username}@auth.invalid`,
          password: credentials.password,
          ...(credentials.username ? { username: credentials.username } : {}),
        })
      );

      authenticated = true;
      if (operation !== this.authOperation) return;

      this._currentUser.set(response.user);
      this.jobsGateway?.reconnect();

      this.permissionService.loadPermissions();
      this.usersMeService.reloadProfile();
      void this.usersMeService.syncServerPreferences();
      void this.notificationGateway?.reconnect();
      this.notificationsService?.refresh();

      await this.router.navigate(['/']);
    } catch (error: unknown) {
      if (operation === this.authOperation) {
        if (!authenticated) this.jobsGateway?.reset();
        const errorMessage = this.extractErrorMessage(error, 'Registration failed. Please try again.');
        this._error.set(errorMessage);
      }
      throw error;
    } finally {
      if (operation === this.authOperation) this._isLoading.set(false);
    }
  }

  /**
   * Open a confirmation dialog and sign the user out only if confirmed.
   * Returns `true` when the user confirmed and the sign-out completed,
   * `false` when the user dismissed the dialog.
   */
  async confirmSignOut(): Promise<boolean> {
    const data: ConfirmDialogData = {
      title: this.translate.instant('auth.logoutConfirm.title'),
      message: this.translate.instant('auth.logoutConfirm.message'),
      confirmText: this.translate.instant('auth.logoutConfirm.confirm'),
      cancelText: this.translate.instant('auth.logoutConfirm.cancel'),
      destructive: true,
    };
    const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, { data });
    const confirmed = await firstValueFrom(dialogRef.closed);
    if (!confirmed) return false;
    await this.signOut();
    return true;
  }

  /**
   * Sign out the current user.
   *
   * Order is important to avoid layout artifacts:
   * 1. Flip `isSigningOut` so guarded layouts hide their protected chrome.
   * 2. Navigate to `/login` while the flag bypasses `publicOnlyGuard`.
   * 3. Clear client-side session state.
   * 4. Call the server-side sign-out endpoint to invalidate the cookie.
   */
  async signOut(): Promise<void> {
    const operation = ++this.authOperation;
    this._isLoading.set(true);
    this._isSigningOut.set(true);
    this._error.set(null);

    try {
      this.jobsGateway?.reset();
      // Navigate first — the layout reacts to `isSigningOut` and stops
      // rendering protected chrome before the route swap completes.
      await this.router.navigate(['/login']);
      if (operation !== this.authOperation) return;

      // Clear client state. By now the user is on /login, so re-renders
      // triggered by these signal updates do not affect protected layouts.
      this._currentUser.set(null);
      this.injector.get(LanguageService).applyCurrentLanguage();
      this.injector.get(ThemeService).applyCurrentTheme();
      this.permissionService.clearPermissions();
      this.notificationGateway?.disconnect();
      this.notificationGateway?.reset();
      this.notificationsService?.clear();

      // Server-side cleanup — invalidates the session cookie.
      await firstValueFrom(this.http.post('/api/auth/sign-out', {}));
    } catch (error: unknown) {
      if (operation === this.authOperation) {
        const errorMessage = this.extractErrorMessage(error, 'Sign out failed.');
        this._error.set(errorMessage);
      }
      throw error;
    } finally {
      if (operation === this.authOperation) {
        this._isSigningOut.set(false);
        this._isLoading.set(false);
      }
    }
  }

  /**
   * Get the current session from the server
   */
  async getSession(): Promise<void> {
    if (this._isSigningOut()) return;
    const operation = ++this.authOperation;
    this._isLoading.set(true);
    this._error.set(null);

    try {
      const response = await firstValueFrom(this.http.get<AuthSession>('/api/auth/get-session'));
      if (operation !== this.authOperation) return;

      this._currentUser.set(response.user);
      this.jobsGateway?.reconnect();

      // Load user permissions after session restore
      this.permissionService.loadPermissions();
      void this.notificationGateway?.reconnect();
      this.notificationsService?.refresh();
    } catch {
      if (operation !== this.authOperation) return;
      this.jobsGateway?.reset();
      // Session doesn't exist or expired - this is not an error
      this._currentUser.set(null);
      this.permissionService.clearPermissions();
    } finally {
      if (operation === this.authOperation) this._isLoading.set(false);
    }
  }

  /**
   * Initiate social/OAuth sign-in.
   *
   * POSTs to better-auth's `/sign-in/social` endpoint and redirects the
   * browser to the OAuth provider's authorization URL returned in the
   * response. The provider will redirect back to `/api/auth/callback/{provider}`
   * after the user authenticates.
   *
   * @param providerId - Social provider identifier (e.g., 'microsoft')
   * @param callbackURL - URL to navigate to after successful OAuth flow (defaults to '/')
   */
  async signInWithSocial(providerId: string, callbackURL = '/'): Promise<void> {
    this._isLoading.set(true);
    this._error.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<{ url: string; redirect: boolean }>('/api/auth/sign-in/social', {
          provider: providerId,
          callbackURL,
          requestSignUp: true,
        })
      );

      if (response.url) {
        globalThis.location.href = response.url;
      }
    } catch (error: unknown) {
      const errorMessage = this.extractErrorMessage(error, 'Social sign-in failed. Please try again.');
      this._error.set(errorMessage);
      this._isLoading.set(false);
      throw error;
    }
  }

  /**
   * Request a password-reset email for the given address.
   *
   * Calls better-auth's `/request-password-reset`. The endpoint always returns a
   * generic success regardless of whether the email matches a user, so this
   * resolves `true` on any 2xx response — callers must not infer account
   * existence. Only a transport-level failure surfaces an error.
   *
   * @param email - The account email to send the reset link to
   */
  async requestPasswordReset(email: string): Promise<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    try {
      await firstValueFrom(this.http.post('/api/auth/request-password-reset', { email }));
      return true;
    } catch (error: unknown) {
      const errorMessage = this.extractErrorMessage(error, 'Could not send the reset email. Please try again.');
      this._error.set(errorMessage);
      return false;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Set a new password using a reset token from the emailed link.
   *
   * Calls better-auth's `/reset-password` with the token and new password.
   * Resolves `true` on success. An invalid or expired token surfaces an error
   * and the promise rejects so the caller can show a token-specific message.
   *
   * @param token       - Raw reset token from the email link
   * @param newPassword - The new password to set
   */
  async resetPassword(token: string, newPassword: string): Promise<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    try {
      await firstValueFrom(this.http.post('/api/auth/reset-password', { token, newPassword }));
      return true;
    } catch (error: unknown) {
      const errorMessage = this.extractErrorMessage(error, 'This reset link is invalid or has expired. Please request a new one.');
      this._error.set(errorMessage);
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Clear any error messages
   */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Extract error message from unknown error object
   */
  private extractErrorMessage(error: unknown, defaultMessage: string): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: { message?: string } };
      return httpError.error?.message || defaultMessage;
    }
    return defaultMessage;
  }
}
